import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Input,
  Label,
  FormFeedback,
  Form,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import bcrypt from "bcryptjs-react";
// Redux
import { useDispatch, useSelector } from "react-redux";
import { addUser as onAddNewUser } from "../../slices/thunks";
//formik
import { useFormik } from "formik";
import * as Yup from "yup";
import { userType } from "../../Components/constants/layout";
import { createSelector } from "reselect";

const FREE_TYPE_CUSTOM_REASON_MAX_LENGTH = 45;

const AddUser = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const [freeTypeDropdown, setFreeTypeDropdown] = useState('');
  const [freeTypeCustomReason, setFreeTypeCustomReason] = useState('');
  
  const selectUsersData = createSelector(
    (state) => state.Users,
    (addSuccess) => addSuccess
  );
  // Inside your component
  const { addSuccess } = useSelector(selectUsersData);

  useEffect(() => {
    if (addSuccess != null) {
      if (addSuccess) {
        validation.resetForm();
        setFreeTypeDropdown('');
        setFreeTypeCustomReason('');
        onClose();
      }
    }
  }, [addSuccess]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      user_id: 0,
      user_email: "",
      user_name: "",
      user_password: "",
      user_token: "",
      user_tokenExpires: null,
      user_apiKey: "",
      user_coupon: "",
      user_emailVerified: false,
      user_subscriptionStatus: 0,
      user_stripeSessionId: "",
      user_stripeCustomerId: "",
      user_stripeDefaultPaymentMethodId: "",
      user_stripePurchasesCustomerId: "",
      user_enabled: true,
      user_type: 4,
      user_billingCycleStart: null,
      user_billingCycleEnd: null,
      user_creationDate: null,
      user_notes: "",
      user_subscriptionStatusText: "",
      user_subscriptionPrice: null,
      user_subscriptionFrequency: "",
      user_free: false,
      user_freeType: null,
      user_lastLoginDate: null,
      user_sellerCloudCompanyId: 0,
      user_showWarning: false,
      user_warningText: "",
    },
    validationSchema: Yup.object({
      user_email: Yup.string()
        .required("Please Enter a User Email")
        .email("Please Enter a Valid Email"),
      user_name: Yup.string().required("Please Enter a User Name"),
      user_password: Yup.string().required("Please Enter a User Password"),
      user_type: Yup.number().required("Please Select a User Type"),
      user_notes: Yup.string(),
      user_enabled: Yup.boolean(),
      user_free: Yup.boolean(),
      user_showWarning: Yup.boolean(),
      user_warningText: Yup.string(),
    }),
    onSubmit: (values) => {
      // Validate freeType manually
      if (values.user_free && freeTypeDropdown === 'Other') {
        if (!freeTypeCustomReason.trim()) {
          validation.setFieldError('user_freeType', 'Please provide a reason for free access');
          return;
        }
        if (freeTypeCustomReason.length > FREE_TYPE_CUSTOM_REASON_MAX_LENGTH) {
          validation.setFieldError('user_freeType', `Free access reason cannot exceed ${FREE_TYPE_CUSTOM_REASON_MAX_LENGTH} characters`);
          return;
        }
      }
      
      const newUser = {
        user_id: 0,
        user_email: values.user_email,
        user_name: values.user_name,
        user_password: bcrypt.hashSync(values.user_password, 10),
        user_token: "",
        user_tokenExpires: null,
        user_apiKey: "",
        user_coupon: "",
        user_emailVerified: true,
        user_subscriptionStatus: 0,
        user_stripeSessionId: "",
        user_stripeCustomerId: "",
        user_stripeDefaultPaymentMethodId: "",
        user_stripePurchasesCustomerId: "",
        user_enabled: values.user_enabled,
        user_type: values.user_type,
        user_billingCycleStart: null,
        user_billingCycleEnd: null,
        user_creationDate: null,
        user_notes: values.user_notes,
        user_subscriptionStatusText: "",
        user_subscriptionPrice: null,
        user_subscriptionFrequency: "",
        user_free: values.user_free,
        user_freeType: values.user_free 
          ? (freeTypeDropdown === 'Other' ? freeTypeCustomReason : (freeTypeDropdown || null))
          : null,
        user_lastLoginDate: null,
        user_sellerCloudCompanyId: 0,
        user_showWarning: values.user_showWarning,
        user_warningText: values.user_warningText || "",
      };
      dispatch(onAddNewUser(newUser));
    },
  });

  return (
    <Modal
      Modal
      isOpen={open}
      toggle={onClose}
      modalClassName="zoomIn"
      centered
      tabIndex="-1"
    >
      <ModalHeader toggle={onClose} className="p-3 bg-primary-subtle">
        <Row className="align-items-center gy-3">
          <div className="col-sm">
            <h5 className="card-title mb-0">Add new User</h5>
          </div>
        </Row>
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <Card>
                <CardBody>
                  <Row>
                    <Col sm={12}>
                      <div className="mb-3">
                        <div className="form-check form-switch form-switch-md align-items-center">
                          <input
                            id="userEnabledSwitch"
                            name="user_enabled"
                            type="checkbox"
                            className="form-check-input"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            checked={validation.values.user_enabled || false}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="userEnabledSwitch"
                          >
                            This user is enabled
                          </label>
                        </div>
                      </div>
                      <div className="mb-3">
                        <Label className="form-label">User Email</Label>
                        <Input
                          name="user_email"
                          type="text"
                          className={
                            validation.touched.user_email &&
                            validation.errors.user_email
                              ? "is-invalid"
                              : ""
                          }
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user_email || ""}
                        />
                        {validation.touched.user_email &&
                        validation.errors.user_email ? (
                          <FormFeedback type="invalid">
                            {validation.errors.user_email}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label className="form-label">User Name</Label>
                        <Input
                          name="user_name"
                          type="text"
                          className={
                            validation.touched.user_name &&
                            validation.errors.user_name
                              ? "is-invalid"
                              : ""
                          }
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user_name || ""}
                        />
                        {validation.touched.user_name &&
                        validation.errors.user_name ? (
                          <FormFeedback type="invalid">
                            {validation.errors.user_name}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label className="form-label">User Password</Label>
                        <Input
                          name="user_password"
                          type="password"
                          className={
                            validation.touched.user_password &&
                            validation.errors.user_password
                              ? "is-invalid"
                              : ""
                          }
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user_password || ""}
                        />
                        {validation.touched.user_password &&
                        validation.errors.user_password ? (
                          <FormFeedback type="invalid">
                            {validation.errors.user_password}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label className="form-label">User Notes</Label>
                        <Input
                          name="user_notes"
                          type="textarea"
                          className={
                            validation.touched.user_notes &&
                            validation.errors.user_notes
                              ? "is-invalid"
                              : ""
                          }
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user_notes || ""}
                        />
                        {validation.touched.user_notes &&
                        validation.errors.user_notes ? (
                          <FormFeedback type="invalid">
                            {validation.errors.user_notes}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label className="form-label">User Type</Label>
                        <Input
                          name="user_type"
                          type="select"
                          className={
                            validation.touched.user_type &&
                            validation.errors.user_type
                              ? "is-invalid"
                              : ""
                          }
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user_type || ""}
                        >
                          <option value="">Select User Type</option>
                          <option value={userType.ADMIN}>Admin</option>
                          <option value={userType.FINALANALYSIS}>
                            Final Analisys
                          </option>
                          <option value={userType.STOCKCHECK}>
                            Stock Check
                          </option>
                          <option value={userType.INITIALANALYSIS}>
                            Initial Analisys
                          </option>
                          <option value={userType.BC}>User</option>
                        </Input>
                        {validation.touched.user_type &&
                        validation.errors.user_type ? (
                          <FormFeedback type="invalid">
                            {validation.errors.user_type}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <div className="form-check form-switch form-switch-md align-items-center">
                          <input
                            name="user_free"
                            disabled={
                              validation.values.user_type != userType.BC
                            }
                            type="checkbox"
                            className="form-check-input"
                            onChange={(e) => {
                              validation.handleChange(e);
                              if (!e.target.checked) {
                                setFreeTypeDropdown('');
                                setFreeTypeCustomReason('');
                              }
                            }}
                            onBlur={validation.handleBlur}
                            checked={validation.values.user_free || false}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="bpaSwitch"
                          >
                            This User is Free
                          </label>
                        </div>
                      </div>
                      {validation.values.user_free && (
                        <div className="mb-3">
                          <Label className="form-label">Free User Type</Label>
                          <Input
                            name="freeTypeDropdown"
                            type="select"
                            className={
                              validation.errors.user_freeType
                                ? "is-invalid"
                                : ""
                            }
                            onChange={(e) => {
                              setFreeTypeDropdown(e.target.value);
                              if (e.target.value !== 'Other') {
                                setFreeTypeCustomReason('');
                              }
                              validation.setFieldError('user_freeType', '');
                            }}
                            value={freeTypeDropdown}
                          >
                            <option value="">Select Free User Type</option>
                            <option value="BPA">BPA</option>
                            <option value="Team">Team</option>
                            <option value="Other">Other</option>
                          </Input>
                          {validation.errors.user_freeType ? (
                            <FormFeedback type="invalid">
                              {validation.errors.user_freeType}
                            </FormFeedback>
                          ) : null}
                        </div>
                      )}
                      {validation.values.user_free &&
                        freeTypeDropdown === 'Other' && (
                          <div className="mb-3">
                            <Input
                              name="freeTypeCustomReason"
                              type="text"
                              placeholder="Add a note on why they get free access"
                              maxLength={FREE_TYPE_CUSTOM_REASON_MAX_LENGTH}
                              className={
                                validation.errors.user_freeType
                                  ? "is-invalid"
                                  : ""
                              }
                              onChange={(e) => {
                                setFreeTypeCustomReason(e.target.value.slice(0, FREE_TYPE_CUSTOM_REASON_MAX_LENGTH));
                                validation.setFieldError('user_freeType', '');
                              }}
                              value={freeTypeCustomReason}
                            />
                            {validation.errors.user_freeType ? (
                              <FormFeedback type="invalid">
                                {validation.errors.user_freeType}
                              </FormFeedback>
                            ) : null}
                          </div>
                        )}
                      <div className="mb-3">
                        <div className="form-check form-switch form-switch-md align-items-center">
                          <input
                            name="user_showWarning"
                            type="checkbox"
                            className="form-check-input"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            checked={
                              validation.values.user_showWarning || false
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="bpaSwitch"
                          >
                            Show Warning to User
                          </label>
                        </div>
                        {validation.values.user_showWarning && (
                          <Input
                            name="user_warningText"
                            type="text"
                            className={
                              validation.touched.user_warningText &&
                              validation.errors.user_warningText
                                ? "is-invalid"
                                : ""
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.user_warningText || ""}
                          />
                        )}
                        {validation.touched.user_warningText &&
                        validation.errors.user_warningText ? (
                          <FormFeedback type="invalid">
                            {validation.errors.user_warningText}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <div className="text-end">
                        <button
                          onClick={onClose}
                          type="button"
                          className="btn btn-danger waves-effect waves-light me-1"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-success waves-effect waves-light "
                        >
                          Create User
                        </button>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Form>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default AddUser;
